import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import unifiedGoogleTokenManager from '../services/UnifiedGoogleTokenManager';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

// Constants (using original patterns)
const DRIVE_API_ENDPOINT = 'https://www.googleapis.com/drive/v3/files';
const TOKEN_EXPIRY = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
const CACHE_KEY = 'handbook_token_cache';
const HANDBOOKS_CACHE_KEY = 'handbooks_cache';
const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes

const getDriveQueryParams = () => ({
  q: "name contains 'SOS' and name contains 'Handbook' and trashed = false",
  fields: 'nextPageToken, files(id, name, modifiedTime, webViewLink)',
  orderBy: 'name',
  pageSize: 1000,
  supportsAllDrives: true,
  includeItemsFromAllDrives: true,
  spaces: 'drive'
});

// Cache helpers (original pattern)
const getHandbooksFromCache = () => {
  try {
    const cache = localStorage.getItem(HANDBOOKS_CACHE_KEY);
    if (cache) {
      const { data, timestamp } = JSON.parse(cache);
      if (Date.now() - timestamp < CACHE_EXPIRY) {
        return data;
      }
    }
  } catch (error) {
    console.error('Cache read error:', error);
  }
  return null;
};

const saveHandbooksToCache = (handbooks) => {
  try {
    const cacheData = {
      data: handbooks,
      timestamp: Date.now()
    };
    localStorage.setItem(HANDBOOKS_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Cache write error:', error);
  }
};

// Token management (original pattern)
const getTokenFromCache = (userId) => {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    const userCache = cache[userId];
    
    if (userCache?.tokenTimestamp && Date.now() - userCache.tokenTimestamp < TOKEN_EXPIRY) {
      return userCache.accessToken;
    }

    if (userCache) {
      delete cache[userId];
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    }
  } catch (error) {
    console.error('Cache read error:', error);
  }
  return null;
};

const saveTokenToCache = (userId, accessToken) => {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    const now = Date.now();
    
    Object.entries(cache).forEach(([id, data]) => {
      if (!data.tokenTimestamp || now - data.tokenTimestamp >= TOKEN_EXPIRY) {
        delete cache[id];
      }
    });

    cache[userId] = {
      accessToken,
      tokenTimestamp: now
    };
    
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('Cache write error:', error);
  }
};

const getNewToken = async (auth) => {
  const provider = new GoogleAuthProvider();
  provider.addScope('https://www.googleapis.com/auth/drive.readonly');
  const result = await signInWithPopup(auth, provider);
  return GoogleAuthProvider.credentialFromResult(result).accessToken;
};

// Refresh token with dialog support
let refreshingToken = null;

const refreshAccessToken = async (auth, currentUser, setShowExpirationDialog) => {
  try {
    if (refreshingToken) {
      return await refreshingToken;
    }

    if (setShowExpirationDialog) {
      return new Promise((resolve, reject) => {
        setShowExpirationDialog({
          open: true,
          onReauthenticate: async () => {
            try {
              // Use unified token manager to authenticate (gets all scopes)
              console.log('🔐 Authenticating with unified token manager...');
              await unifiedGoogleTokenManager.authenticate(currentUser.uid, false);
              
              // Get the token from unified manager
              const newToken = await unifiedGoogleTokenManager.getToken(currentUser.uid, 'drive');
              
              // Also save to legacy cache for backward compatibility
              saveTokenToCache(currentUser.uid, newToken);
              
              setShowExpirationDialog({ open: false });
              resolve(newToken);
            } catch (error) {
              console.error('Authentication failed:', error);
              if (error.message === 'Authentication cancelled by user') {
                setShowExpirationDialog({ open: false });
                reject(new Error('auth_cancelled'));
              } else {
                reject(error);
              }
            }
          },
          onClose: () => {
            setShowExpirationDialog({ open: false });
            reject(new Error('auth_cancelled'));
          }
        });
      });
    }

    // If no dialog, try unified token manager first
    try {
      console.log('🔐 Attempting silent authentication with unified token manager...');
      await unifiedGoogleTokenManager.authenticate(currentUser.uid, false);
      const newToken = await unifiedGoogleTokenManager.getToken(currentUser.uid, 'drive');
      saveTokenToCache(currentUser.uid, newToken);
      return newToken;
    } catch (unifiedError) {
      // Fallback to old method
      console.log('⚠️ Unified auth failed, falling back to legacy method');
      const newToken = await getNewToken(auth);
      saveTokenToCache(currentUser.uid, newToken);
      return newToken;
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    throw error;
  }
};

// Fetch Drive files with retry logic
const fetchDriveFiles = async (accessToken, auth, currentUser, retryCount = 0) => {
  try {
    const response = await fetch(
      `${DRIVE_API_ENDPOINT}?${new URLSearchParams(getDriveQueryParams())}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Drive API Error:', errorText);
      
      if ((response.status === 401 || response.status === 403) && retryCount < 1) {
        console.log('Token expired, refreshing...');
        const newToken = await refreshAccessToken(auth, currentUser, null);
        return fetchDriveFiles(newToken, auth, currentUser, retryCount + 1);
      }
      
      throw new Error(response.status === 401 || response.status === 403 ? 'token_expired' : 'fetch_failed');
    }
    
    return response.json();
  } catch (error) {
    if (error.message === 'token_expired' && retryCount < 1) {
      console.log('Token expired, refreshing...');
      const newToken = await refreshAccessToken(auth, currentUser, null);
      return fetchDriveFiles(newToken, auth, currentUser, retryCount + 1);
    }
    throw error;
  }
};

// Token Expiration Dialog Component
const TokenExpirationDialog = ({ open, onReauthenticate, onClose }) => (
  <Dialog 
    open={open} 
    onClose={onClose}
    PaperProps={{
      sx: {
        borderRadius: 2,
        p: 1,
        maxWidth: 400
      }
    }}
  >
    <DialogTitle sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1.5,
      pb: 1
    }}>
      <Box sx={{
        width: 40,
        height: 40,
        borderRadius: 1,
        bgcolor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'common.white'
      }}>
        <LockIcon />
      </Box>
      Google Drive Access Required
    </DialogTitle>
    <DialogContent>
      <Typography variant="body1" sx={{ mb: 2 }}>
        To access the handbook library, you need to connect your Google Drive account.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        We only request read-only access to view your handbooks. Your data remains secure.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3 }}>
      <Button 
        onClick={onClose}
        variant="outlined"
        sx={{ borderRadius: 2 }}
      >
        Cancel
      </Button>
      <Button
        onClick={onReauthenticate}
        variant="contained"
        sx={{ borderRadius: 2 }}
        autoFocus
      >
        Connect Google Drive
      </Button>
    </DialogActions>
  </Dialog>
);

/**
 * Enhanced Google Drive hook with backward compatibility
 * Uses the original authentication pattern but with enhanced UI
 */
export const useEnhancedGoogleDrive = (options = {}) => {
  const [handbooks, setHandbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExpirationDialog, setShowExpirationDialog] = useState({ open: false });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user } = useAuth();

  // Analytics state (simplified)
  const [analytics, setAnalytics] = useState({
    requests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    errors: 0
  });

  const fetchHandbooks = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user) {
        setLoading(false);
        return;
      }

      // Try cache first
      if (!forceRefresh) {
        const cachedHandbooks = getHandbooksFromCache();
        if (cachedHandbooks) {
          setHandbooks(cachedHandbooks);
          setAnalytics(prev => ({ ...prev, cacheHits: prev.cacheHits + 1 }));
          setLoading(false);
          return;
        }
      } else {
        localStorage.removeItem(HANDBOOKS_CACHE_KEY);
      }

      setAnalytics(prev => ({ ...prev, cacheMisses: prev.cacheMisses + 1 }));

      // Try to get token from unified token manager first
      let accessToken = null;
      let auth = null;
      let currentUser = null;
      
      console.log('🔍 Checking for token in unified token manager for user:', user.uid);
      
      try {
        accessToken = await unifiedGoogleTokenManager.getToken(user.uid, 'drive');
        console.log('✅ Found token in unified token manager');
      } catch (unifiedError) {
        console.log('⚠️ Unified token manager check (drive scope):', unifiedError.message);
        
        // Try combined scope as fallback
        try {
          accessToken = await unifiedGoogleTokenManager.getToken(user.uid, 'combined');
          console.log('✅ Found token in unified token manager (combined scope)');
        } catch (combinedError) {
          console.log('⚠️ No token in unified manager, checking legacy cache...');
          
          // Fallback to old cache system (requires Firebase auth)
          auth = getAuth();
          currentUser = auth.currentUser;
          
          if (!currentUser) {
            throw new Error('User not authenticated');
          }
          
          accessToken = getTokenFromCache(currentUser.uid);
          
          if (!accessToken) {
            console.log('⚠️ No token in legacy cache either, showing authentication dialog...');
            accessToken = await refreshAccessToken(auth, currentUser, setShowExpirationDialog);
          }
        }
      }

      try {
        setAnalytics(prev => ({ ...prev, requests: prev.requests + 1 }));
        // Get auth/currentUser if not already set (for fetchDriveFiles which needs them for retry logic)
        if (!auth || !currentUser) {
          auth = getAuth();
          currentUser = auth.currentUser;
        }
        const data = await fetchDriveFiles(accessToken, auth, currentUser);
        const sortedHandbooks = data.files.sort((a, b) => a.name.localeCompare(b.name));
        setHandbooks(sortedHandbooks);
        saveHandbooksToCache(sortedHandbooks);
        setLoading(false);
      } catch (error) {
        if (error.message === 'token_expired') {
          accessToken = await refreshAccessToken(auth, currentUser, setShowExpirationDialog);
          const data = await fetchDriveFiles(accessToken, auth, currentUser);
          const sortedHandbooks = data.files.sort((a, b) => a.name.localeCompare(b.name));
          setHandbooks(sortedHandbooks);
          saveHandbooksToCache(sortedHandbooks);
          setLoading(false);
        } else {
          throw error;
        }
      }
    } catch (err) {
      console.error('Error loading handbooks:', err);
      if (err.message !== 'auth_cancelled') {
        setError(err);
        setAnalytics(prev => ({ ...prev, errors: prev.errors + 1 }));
      }
      setLoading(false);
    }
  }, [user]);

  const refreshHandbooks = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchHandbooks(true);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchHandbooks]);

  const searchHandbooks = useCallback(async (query) => {
    // Simple client-side search for now
    return handbooks.filter(handbook =>
      handbook.name.toLowerCase().includes(query.toLowerCase())
    );
  }, [handbooks]);

  const clearCache = useCallback(async () => {
    localStorage.removeItem(HANDBOOKS_CACHE_KEY);
    await fetchHandbooks(true);
  }, [fetchHandbooks]);

  const getPerformanceMetrics = useCallback(() => {
    return {
      analytics,
      handbookCount: handbooks.length,
      networkStatus: navigator.onLine ? 'online' : 'offline',
      lastUpdated: new Date().toISOString()
    };
  }, [analytics, handbooks.length]);

  // Initialize on mount
  useEffect(() => {
    let mounted = true;

    const initializeAsync = async () => {
      if (mounted) {
        await fetchHandbooks();
      }
    };

    initializeAsync();

    return () => {
      mounted = false;
    };
  }, [user, fetchHandbooks]);

  // Authentication Dialog Component
  const TokenExpirationDialogComponent = showExpirationDialog.open && (
    <TokenExpirationDialog 
      open={showExpirationDialog.open}
      onReauthenticate={showExpirationDialog.onReauthenticate}
      onClose={showExpirationDialog.onClose}
    />
  );

  return {
    // Data
    handbooks,
    loading,
    error,
    networkStatus: navigator.onLine ? 'online' : 'offline',
    
    // Actions
    refreshHandbooks,
    searchHandbooks,
    clearCache,
    
    // Analytics
    analytics,
    getPerformanceMetrics,
    
    // Components
    TokenExpirationDialog: TokenExpirationDialogComponent,
    
    // Status
    isOnline: navigator.onLine,
    hasData: handbooks.length > 0,
    cacheHitRate: analytics.cacheHits / (analytics.cacheHits + analytics.cacheMisses) || 0,
    errorRate: analytics.errors / Math.max(analytics.requests, 1) || 0,
    
    // Loading states
    isRefreshing
  };
};

export default useEnhancedGoogleDrive;